import apiClient from "./ApiClient";

/**
 * Add multiple user subscription schedules
 * @param {Array} scheduleList - Array of UserSubscriptionScheduleRequestDto objects
 * @returns {Promise<void>}
 */
export const addAllUserSubscriptionSchedules = async (scheduleList) => {
  return apiClient
    .post("/user-subscription-schedules/batch", scheduleList)
    .then((res) => res.data);
};

/**
 * Get user subscription schedules by user subscription ID
 * @param {number} userSubscriptionId - User subscription ID
 * @returns {Promise<Array>} Array of UserSubscriptionScheduleResponseDto
 */
export const getUserSubscriptionSchedulesByUserSubscriptionId = async (
  userSubscriptionId
) => {
  return apiClient
    .get(`/user-subscription-schedules/userSubscription/${userSubscriptionId}`)
    .then((res) => res.data);
};

/**
 * Get next user subscription schedules by user ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} NextSubscriptionScheduleResponse
 */
export const getNextUserSubscriptionSchedulesByUserId = async (userId) => {
  return apiClient
    .get(`/user-subscription-schedules/next/${userId}`)
    .then((res) => res.data);
};

/**
 * Update location for user subscription schedule
 * @param {number} userSubscriptionScheduleId - User subscription schedule ID
 * @param {number} locationId - Location ID
 * @returns {Promise<void>}
 */
export const updateLocationForUserSubscriptionSchedule = async (
  userSubscriptionScheduleId,
  locationId
) => {
  return apiClient
    .get(
      `/user-subscription-schedules/update/location/${userSubscriptionScheduleId}/${locationId}`
    )
    .then((res) => res.data);
};

/**
 * Get user subscription schedules by restaurant ID and date
 * @param {number} restaurantId - Restaurant ID
 * @param {string} date - Date in ISO format (YYYY-MM-DD)
 * @param {string} language - Language preference (default: 'en')
 * @returns {Promise<Array>} Array of NextSubscriptionScheduleResponse
 */
export const getUserSubscriptionSchedulesByRestaurantIdAndDate = async (
  restaurantId,
  date,
  language = "en"
) => {
  return apiClient
    .get(`/user-subscription-schedules/restaurant/${restaurantId}/date/${date}`, {
      headers: {
        'Accept-Language': language
      }
    })
    .then((res) => res.data);
};

/**
 * Update status for user subscription schedule
 * @param {number} userSubscriptionScheduleId - User subscription schedule ID
 * @param {string} status - New status (SubscriptionScheduleStatus enum value)
 * @returns {Promise<void>}
 */
export const updateStatusForUserSubscriptionSchedule = async (
  userSubscriptionScheduleId,
  status
) => {
  return apiClient
    .patch(`/user-subscription-schedules/update/status/${userSubscriptionScheduleId}?status=${status}`)
    .then((res) => res.data);
};

/**
 * Helper function to format date for API
 * @param {Date} date - JavaScript Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForAPI = (date) => {
  if (!date) return "";

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Get today's subscription schedules for a restaurant
 * @param {number} restaurantId - Restaurant ID
 * @param {string} language - Language preference (default: 'en')
 * @returns {Promise<Array>} Array of NextSubscriptionScheduleResponse
 */
export const getTodaySubscriptionSchedules = async (
  restaurantId,
  language = "en"
) => {
  const today = formatDateForAPI(new Date());
  return getUserSubscriptionSchedulesByRestaurantIdAndDate(
    restaurantId,
    today,
    language
  );
};

/**
 * Get subscription schedules for a specific date range
 * @param {number} restaurantId - Restaurant ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} language - Language preference (default: 'en')
 * @returns {Promise<Array>} Array of subscription schedules for the date range
 */
export const getSubscriptionSchedulesForDateRange = async (
  restaurantId,
  startDate,
  endDate,
  language = "en"
) => {
  const promises = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const dateStr = formatDateForAPI(currentDate);
    promises.push(
      getUserSubscriptionSchedulesByRestaurantIdAndDate(
        restaurantId,
        dateStr,
        language
      )
    );
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const results = await Promise.all(promises);
  return results.flat(); // Flatten the array of arrays
};
